<template>
    <div class="o-module u-w100 u-py20 u-overflowYAuto">
        <div class="o-module__inner u-container u-overflowHidden u-paddingButtonSize">
            <div class="o-module__info" v-if="!isModuleStarted">
                <div class="o-module__top">
                    <div class="o-module__imgHolder u-posRelative u-br u-overflowHidden" v-if="module.module_img">
                        <img :src="module.module_img" class="o-module__img u-cover">
                        <div class="o-module__tags">
                            <div class="a-tag -green">{{ progressPercentage }}%</div>
                            <div class="a-tag" v-if="module.module_duration">
                                <i class='icon bx bx-time-five'></i> 
                                <span>{{ module.module_duration }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="o-module__overlay" v-if="!module.completed">
                        <div v-if="moduleWaitingForFeedback && (moduleRating === 0 || moduleRating === 1)">
                            
                            <div v-if="moduleRating === 0" class="a-tag -orange">{{ $t('module.failed') }}</div>
                            <div v-if="moduleRating === 1" class="a-tag -green">{{ $t('module.passed') }}</div>
                        </div>
                        <div v-else-if="moduleWaitingForFeedback && moduleRating === false">
                            <div class="a-tag -yellow">{{ $t('module.waitingForFeedback') }}</div>
                        </div>
                        <div v-else>
                            <div class="a-tag" v-if="module.date_completed_by">
                                <span v-if="new Date(module.date_completed_by) > new Date(module.current_timestamp)">
                                    {{ $t('module.toBeCompletedBy') }}: 
                                    <span class="u-colorSecondary u-fw500">{{ formatDate(module.date_completed_by) }}</span>
                                </span>
                                <span v-else>
                                    <div class="a-tag -red">{{ $t('module.completionDatePassed') }}</div>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <Tabs v-model="activeTab" :tabs="tabsData">
                    <template #default="{ activeTab }">
                        <div class="o-module__general" v-if="activeTab === 0" v-html="module.module_information"></div>
                        <div class="o-module__learningsList" v-if="activeTab === 1 && introSteps.length > 0">
                            <ol class="m-list u-fw500 u-listUnstyled">
                                <li v-for="(step, index) in introSteps" :key="index" class="m-list__item -grid">
                                    <div class="a-circle -small">{{ step.introCount }}</div>
                                    {{ step.title }}
                                    <div v-if="step.isFinished" class="a-circle -tiny -green">
                                        <i class='bx bx-check' ></i>
                                    </div>
                                </li>
                            </ol>
                        </div>
                        <div class="o-module__attachments" v-if="activeTab === 2">
                            <ul class="o-module__attachmentsList m-list -borders u-listUnstyled u-fw500" v-if="module.module_attachments && module.module_attachments.length > 0">
                                <li class="m-list__item" v-for="attachment in module.module_attachments" :key="attachment.attachment.ID">
                                    <a class="m-list__link -grid" :href="attachment.attachment.url" target="_blank">
                                        {{ attachment.attachment.title }}
                                        <i class='bx bx-download u-colorPrimary'></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </template>
                </Tabs>
            </div>
            <div class="o-module__steps">
                <Step 
                    v-for="(step, index) in steps" 
                    :key="step.uuid" 
                    :step="step"
                    :moduleTitle="module.title"
                    :isVisible="isModuleStarted && (step === firstIncompleteStep)"
                    v-model:attempts="attempts"
                />
            </div>
        </div>
    </div>
    <FixedButton v-if="isButtonVisible && new Date(module.date_completed_by) > new Date(module.current_timestamp)" @click="startModule">
        {{ $t('module.continueModule') }}
    </FixedButton>
    <Notice 
        :title="notice.info.title" 
        :message="notice.info.message" 
        :state="notice.info.state" 
        :isVisible="notice.isVisible"
    />
    
</template>

<script setup>
import { ref, onMounted, provide, computed, watch, reactive, watchEffect } from 'vue';
import { inject } from 'vue';
import { debounce } from 'lodash';
import api from '../services/api';
import { calculateProgress, calculateProgressPercentage, formatDate } from '../utils/globalFunctions';
import { useI18n } from 'vue-i18n';

// Components
import FixedButton from '../components/molecules/FixedButton.vue';
import Step from '../components/organisms/Step.vue';
import Notice from '../components/molecules/Notice.vue';
import Tabs from '../components/molecules/Tabs.vue';

const { t } = useI18n();
const currentId = inject('currentId');
const module = ref([]);
const headerTitle = ref('');
const steps = ref([]);
const isModuleStarted = ref(false);
const isModuleLoaded = ref(false);
const isButtonVisible = ref(false);
const moduleWaitingForFeedback = ref(false);
const moduleRating = ref(false);
const doNotMoveToNextStep = ref(false);
const activeTab = ref(0);
const tabsData = [
    { label: t('module.general') },
    { label: t('module.learnings') },
    { label: t('module.attachments') }
];

const attempts = ref([]);
const emit = defineEmits(['updateHeader']);

const notice = ref({
    info: {
        title: '',
        message: '',
        state: ''
    },
    isVisible: false
});

const introSteps = computed(() => {
    let introCount = 0;
    return steps.value
        .filter(step => step.type === 'intro')
        .map(step => {
            introCount++;
            const stepIndex = steps.value.findIndex(s => s.uuid === step.uuid);
            const nextIntroIndex = steps.value.findIndex((s, i) => i > stepIndex && s.type === 'intro');
            const overlappingSteps = steps.value.slice(stepIndex + 1, nextIntroIndex === -1 ? undefined : nextIntroIndex);
            const isFinished = overlappingSteps.every(s => s.completed);
            return {
                title: step.title,
                isFinished: isFinished,
                introCount: introCount
            };
        });
});

const progress = computed(() => {
    return {
        [module.value.id]: steps.value.map(step => ({
            uuid: step.uuid,
            completed: step.completed
        }))
    };
});

const moduleState = reactive({
    isStarted: false,
    currentStep: {
        title: '',
        introCount: 0
    },
    progress: {
        completed: 0,
        total: 0
    }
});

const currentStepInfo = computed(() => {
    if (!moduleState.isStarted) {
        return { title: headerTitle.value, introCount: 0 };
    }

    let introCount = 0;
    let lastIntroTitle = '';
    let currentStepFound = false;

    for (const step of steps.value) {
        if (step.type === 'intro') {
            introCount++;
            lastIntroTitle = step.title;
        } else if (!step.completed && !currentStepFound) {
            currentStepFound = true;
            return { title: step.title, introCount };
        }
    }

    return { title: lastIntroTitle || headerTitle.value, introCount };
});

const simpleProgress = computed(() => {
    const { completed, total } = calculateProgress(steps.value);
    moduleState.progress = { completed, total };
    return [completed, total];
});

const firstIncompleteStep = computed(() => {
    if (doNotMoveToNextStep.value == true) {
        return steps.value.slice().reverse().find(step => step.completed);
    } else {
        return steps.value.find(step => !step.completed);
    }
});

watchEffect(() => {
    if (isModuleLoaded.value && !isModuleStarted.value) {
        const [completed, total] = simpleProgress.value;
        isButtonVisible.value = !(completed !== 0 && total !== 0 && completed === total);

        if (!module.value.completed) {
            if (module.value.module_rating !== false) {
                moduleRating.value = module.value.module_rating;
                moduleWaitingForFeedback.value = true;
            } else {
                moduleWaitingForFeedback.value = (completed !== 0 && total !== 0 && completed === total);
            }
        }
    }
});

const completeStep = async (uuid, moveToNextStep = false, pushProgress = true) => {
    if (moveToNextStep) {
        doNotMoveToNextStep.value = false;
        updateHeaderInfo();
    } else {
        doNotMoveToNextStep.value = true;
    }

    const stepToComplete = steps.value.find(step => step.uuid === uuid);

    if (stepToComplete) {
        stepToComplete.completed = true;
        simpleProgress.value;
        moduleState.currentStep = currentStepInfo.value;

        if (pushProgress) {
            await debouncedPushStepsPromise();
        }
    }
};

const debouncedPushStepsPromise = () => {
    return new Promise((resolve, reject) => {
        debouncedPushSteps(resolve, reject);
    });
};

const debouncedPushSteps = debounce(async (resolve, reject) => {
    try {
        const response = await api.post(`/module/${currentId.value}/update-progress`, {
            progress: progress.value
        });
        
        if (response.status === 200) {
            resolve();
        } else if (response.status === 403)  {
            alert( t('module.failedUpdateProgress') );
            reject('Failed to update progress');
            window.location.hash = '#/modules';
        } else {
            reject('Failed to update progress');
        }
    } catch (error) {
        alert( t('module.failedUpdateProgress') );
        reject(error);
        window.location.hash = '#/modules';
    }
}, 1000);

const debouncedPushAttempts = debounce(async () => {

    try {
        const response = await api.post(`/module/${currentId.value}/add-attempts`, {
            attempts: attempts.value
        });
        
        if (response.status === 200) {

        }
    } catch (error) {
        console.error('Error updating attempts:', error);
    }
}, 500);

watch(attempts, () => {

    debouncedPushAttempts();

}, { deep: true });

const showNotice = (title, message, state) => {
    notice.value.info.title = title;
    notice.value.info.message = message;
    notice.value.info.state = state;
    notice.value.isVisible = true;
};

const hideNotice = () => {
    notice.value.isVisible = false;
};

provide('completeStep', completeStep);
provide('showNotice', showNotice);
provide('hideNotice', hideNotice);

watch(moduleState, () => {
    if (moduleState.isStarted) {
        updateHeaderInfo();
    }
}, { deep: true });

watch(currentStepInfo, (newValue) => {
    if (moduleState.isStarted) {
        moduleState.currentStep = newValue;
        updateHeaderInfo();
    }
});

const startModule = () => {
    isModuleStarted.value = true;
    isButtonVisible.value = false;
    moduleState.isStarted = true;
    
    const initialStep = currentStepInfo.value;
    moduleState.currentStep = initialStep;

    updateHeaderInfo();
};

const updateHeaderInfo = () => {
    if( doNotMoveToNextStep.value == false ) {
        const { title, introCount } = moduleState.isStarted ? currentStepInfo.value : { title: headerTitle.value, introCount: 0 };
        const [completed, total] = simpleProgress.value;
        emit('updateHeader', title, introCount, [completed, total], progressPercentage.value, moduleState.isStarted);
    }
};

onMounted(() => {
    fetchModule();
});

const fetchModule = async () => {
    try {
        const response = await api.get(`/module/${currentId.value}`);

        if (response.status === 200 && response.data.length !== 0) {
            module.value = response.data;
            headerTitle.value = module.value.title;
            steps.value = module.value.micro_learnings;
            isModuleLoaded.value = true;
            simpleProgress.value;
            updateHeaderInfo();
        }
        
    } catch (error) {
        console.error('Error fetching module:', error);
    }
};

const progressPercentage = computed(() => {
    const [completed, total] = simpleProgress.value;
    return calculateProgressPercentage(completed, total);
});

</script>
